import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Save, Clock, AlertTriangle, Printer } from 'lucide-react';
import { toast } from 'sonner';

const SystemSettingsTab = ({ user }) => {
  const [settings, setSettings] = useState({
    session: {
      timeout_minutes: 5,
      warning_seconds: 30
    },
    report_access: {
      default_duration_hours: 2
    },
    print_settings: {
      show_item_rate: true
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axiosInstance.get('/settings/system');
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axiosInstance.put('/settings/system', settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
            ? error.response.data.detail 
            : JSON.stringify(error.response.data.detail))
        : 'Failed to save settings';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6 rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Clock className="w-6 h-6" />
          System Settings
        </h2>
        <p className="text-amber-100 mt-1">Configure global system behavior</p>
      </div>

      {/* Session Timeout Settings */}
      <div className="bg-white rounded-xl shadow-md border-2 border-amber-200 p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          ⏱️ Session Timeout Configuration
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Auto-Logout Timeout (minutes)
            </label>
            <select
              value={settings.session.timeout_minutes}
              onChange={(e) => setSettings({
                ...settings,
                session: { ...settings.session, timeout_minutes: parseInt(e.target.value) }
              })}
              className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none"
            >
              <option value={2}>2 minutes</option>
              <option value={5}>5 minutes</option>
              <option value={10}>10 minutes</option>
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Users will be automatically logged out after this period of inactivity
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Warning Before Logout (seconds)
            </label>
            <select
              value={settings.session.warning_seconds}
              onChange={(e) => setSettings({
                ...settings,
                session: { ...settings.session, warning_seconds: parseInt(e.target.value) }
              })}
              className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none"
            >
              <option value={30}>30 seconds</option>
              <option value={60}>60 seconds</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Warning popup will appear this many seconds before auto-logout
            </p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Current Configuration:</p>
                <p>Users will see a warning after <strong>{settings.session.timeout_minutes - (settings.session.warning_seconds / 60)} minutes</strong> of inactivity,</p>
                <p>and will be logged out after <strong>{settings.session.timeout_minutes} minutes</strong> if no action is taken.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Report Access Settings */}
      <div className="bg-white rounded-xl shadow-md border-2 border-amber-200 p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          📊 Report Access Configuration
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Default Access Duration (hours)
            </label>
            <select
              value={settings.report_access.default_duration_hours}
              onChange={(e) => setSettings({
                ...settings,
                report_access: { ...settings.report_access, default_duration_hours: parseFloat(e.target.value) }
              })}
              className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none"
            >
              <option value={0.167}>10 minutes</option>
              <option value={0.333}>20 minutes</option>
              <option value={0.5}>30 minutes</option>
              <option value={1}>1 hour</option>
              <option value={2}>2 hours</option>
              <option value={4}>4 hours</option>
              <option value={8}>8 hours</option>
              <option value={24}>24 hours</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              When granting report access to staff, this will be the default duration
            </p>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">Temporary Access:</p>
                <p>Report access granted to staff will automatically expire after the configured duration.</p>
                <p className="mt-1">Staff users will need to be granted access again after expiry.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print Settings */}
      <div className="bg-white rounded-xl shadow-md border-2 border-amber-200 p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Printer className="w-5 h-5 text-amber-600" />
          Print Settings
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div>
              <label className="block text-sm font-semibold text-gray-800">
                Show Item-wise Rate in Print
              </label>
              <p className="text-xs text-gray-500 mt-1">
                When enabled, each item's per-gram rate will be displayed in all print formats (A4, Thermal, Summary)
              </p>
            </div>
            <button
              data-testid="toggle-show-item-rate"
              type="button"
              onClick={() => setSettings({
                ...settings,
                print_settings: {
                  ...settings.print_settings,
                  show_item_rate: !(settings.print_settings?.show_item_rate ?? true)
                }
              })}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                (settings.print_settings?.show_item_rate ?? true) ? 'bg-amber-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                  (settings.print_settings?.show_item_rate ?? true) ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <div className="text-sm text-blue-800">
              <p><strong>Currently:</strong> Item-wise rate is <strong>{(settings.print_settings?.show_item_rate ?? true) ? 'visible' : 'hidden'}</strong> in print outputs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Save Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SystemSettingsTab;
